import { useEffect, useMemo, useState } from "react";
import api, { formatDateIT } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, Download, FileText, RefreshCw, XCircle } from "lucide-react";

const ACTION_LABELS = {
  login_success: "Accesso riuscito",
  login_failed: "Accesso fallito",
  logout: "Uscita",
  password_changed: "Cambio password",
  password_change_failed: "Cambio password fallito",
  password_reset_requested: "Richiesta reset password",
  password_reset_completed: "Reset password completato",
  admin_password_reset: "Reset password (admin)",
  password_hash_migrated: "Upgrade hash password → Argon2id",
  user_created: "Utente creato",
  user_updated: "Utente aggiornato",
  bootstrap_production: "Ambiente produzione preparato",
  create_case: "Pratica creata",
  status_change: "Cambio stato prodotto",
  delivery: "Riconsegna al cliente",
  quotation_created: "Preventivo creato",
  customer_approval: "Autorizzazione cliente",
  audit_export_csv: "Esportazione CSV audit",
  audit_export_pdf: "Esportazione PDF audit",
};

const label = (a) => ACTION_LABELS[a] || a;

export default function AuditLog() {
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [actions, setActions] = useState({});
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ date_from: "", date_to: "", user_id: "all", action: "all", target: "", success: "all", ip: "" });

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v && v !== "all") params.append(k, v); });
      const r = await api.get(`/audit/logs?${params.toString()}&limit=200`);
      setItems(r.data.items || []);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    api.get("/users").then((r) => setUsers(r.data));
    api.get("/audit/actions").then((r) => setActions(r.data));
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const allActions = useMemo(() => Object.values(actions).flat(), [actions]);

  const exportFile = async (kind) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v && v !== "all") params.append(k, v); });
    const token = localStorage.getItem("ama_token");
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/audit/export.${kind}?${params.toString()}`;
    const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await r.blob();
    const u = URL.createObjectURL(blob);
    if (kind === "csv") { const a = document.createElement("a"); a.href = u; a.download = "ama_audit.csv"; a.click(); }
    else window.open(u, "_blank");
  };

  return (
    <div className="p-4 md:p-8 max-w-6xl mx-auto space-y-5" data-testid="audit-page">
      <div>
        <div className="text-xs uppercase tracking-widest text-[#d63384] font-semibold">Impostazioni</div>
        <h1 className="text-3xl md:text-4xl font-heading font-bold text-[#0a0a2e]">Registro attività</h1>
        <p className="text-slate-500">Cronologia immutabile degli eventi di sicurezza e operativi. Solo lettura.</p>
      </div>

      {/* Filters */}
      <Card className="ama-card border-0 shadow-none">
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-3">
            <div><Label>Da</Label><Input data-testid="audit-date-from" type="date" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} /></div>
            <div><Label>A</Label><Input data-testid="audit-date-to" type="date" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} /></div>
            <div>
              <Label>Utente</Label>
              <Select value={filters.user_id} onValueChange={(v) => setFilters({ ...filters, user_id: v })}>
                <SelectTrigger data-testid="audit-user"><SelectValue placeholder="Tutti" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value="all">Tutti</SelectItem>
                  {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.full_name} · {u.email}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Azione</Label>
              <Select value={filters.action} onValueChange={(v) => setFilters({ ...filters, action: v })}>
                <SelectTrigger data-testid="audit-action"><SelectValue placeholder="Tutte" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value="all">Tutte</SelectItem>
                  {allActions.map((a) => <SelectItem key={a} value={a}>{label(a)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Oggetto / Record</Label><Input data-testid="audit-target" placeholder="ID, numero pratica, email…" value={filters.target} onChange={(e) => setFilters({ ...filters, target: e.target.value })} /></div>
            <div><Label>IP</Label><Input data-testid="audit-ip" value={filters.ip} onChange={(e) => setFilters({ ...filters, ip: e.target.value })} /></div>
            <div>
              <Label>Esito</Label>
              <Select value={filters.success} onValueChange={(v) => setFilters({ ...filters, success: v })}>
                <SelectTrigger data-testid="audit-success"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti</SelectItem>
                  <SelectItem value="true">Successo</SelectItem>
                  <SelectItem value="false">Fallimento</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end gap-2">
              <Button data-testid="audit-apply" onClick={load} className="ama-btn-primary w-full"><RefreshCw className="w-4 h-4 mr-1" /> Applica</Button>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 mt-4">
            <Button data-testid="audit-export-csv" variant="outline" size="sm" onClick={() => exportFile("csv")}><Download className="w-4 h-4 mr-1" /> Esporta CSV</Button>
            <Button data-testid="audit-export-pdf" variant="outline" size="sm" onClick={() => exportFile("pdf")}><FileText className="w-4 h-4 mr-1" /> Esporta PDF</Button>
            <div className="text-xs text-slate-500 ml-auto self-center">{items.length} eventi mostrati (max 200 per pagina)</div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="ama-card border-0 shadow-none">
        <CardContent className="pt-6">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                  <th className="py-2 px-3">Data/Ora</th>
                  <th className="py-2 px-3">Azione</th>
                  <th className="py-2 px-3">Utente</th>
                  <th className="py-2 px-3">Oggetto</th>
                  <th className="py-2 px-3">IP</th>
                  <th className="py-2 px-3">Esito</th>
                  <th className="py-2 px-3">Dettaglio</th>
                </tr>
              </thead>
              <tbody>
                {items.map((e) => (
                  <tr key={e.id} className="border-t border-slate-100 hover:bg-fuchsia-50/40" data-testid={`audit-row-${e.action}`}>
                    <td className="py-2 px-3 whitespace-nowrap text-xs">{formatDateIT(e.at)}</td>
                    <td className="py-2 px-3 whitespace-nowrap"><Badge variant="outline" className="text-[10px]">{label(e.action)}</Badge></td>
                    <td className="py-2 px-3 text-xs">{e.user_name || e.user_email || <span className="text-slate-400 italic">-</span>}</td>
                    <td className="py-2 px-3 text-xs font-mono truncate max-w-[220px]">{e.target || "-"}</td>
                    <td className="py-2 px-3 text-xs font-mono">{(e.meta || {}).ip || "-"}</td>
                    <td className="py-2 px-3">{e.success !== false ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <XCircle className="w-4 h-4 text-rose-600" />}</td>
                    <td className="py-2 px-3 text-xs text-slate-500">{JSON.stringify(e.meta || {})}</td>
                  </tr>
                ))}
                {items.length === 0 && <tr><td colSpan={7} className="py-8 text-center text-slate-500 italic">{loading ? "Caricamento…" : "Nessun evento con questi filtri."}</td></tr>}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <div className="text-xs text-slate-400 text-center">Il registro attività è di sola lettura. Gli eventi non possono essere modificati o cancellati dall'interfaccia. Password, token e chiavi non sono mai registrati.</div>
    </div>
  );
}
